<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  label: { type: String, required: true },
  value: { type: String, default: '' },
  icon: { type: String, default: '' },
  inputType: { type: String, default: 'text' },
  href: { type: String, default: '' },
  required: { type: Boolean, default: false },
  showCopy: { type: Boolean, default: false },
  editable: { type: Boolean, default: true },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const isEditing = ref(false);
const editedValue = ref(props.value || '');

watch(
  () => props.value,
  value => {
    editedValue.value = value || '';
  }
);

const startEditing = () => {
  if (!props.editable) return;
  editedValue.value = props.value || '';
  isEditing.value = true;
};

const stopEditing = () => {
  isEditing.value = false;
  editedValue.value = props.value || '';
};

const save = () => {
  const nextValue = editedValue.value.trim();
  if (props.required && !nextValue) return;

  isEditing.value = false;
  if (nextValue !== (props.value || '')) {
    emit('update', nextValue);
  }
};

const onCopy = async event => {
  event.stopPropagation();
  await copyTextToClipboard(props.value);
  useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
};
</script>

<template>
  <div
    class="flex items-center justify-between w-full min-h-11 gap-2 px-3 py-2.5 group"
  >
    <div class="flex items-center flex-shrink min-w-0 gap-2.5">
      <span
        v-if="icon"
        class="flex-shrink-0 size-4 text-n-slate-11"
        :class="icon"
      />
      <span class="text-sm font-normal truncate text-n-slate-12">
        {{ label }}
      </span>
    </div>

    <div
      v-if="isEditing"
      v-on-clickaway="stopEditing"
      class="flex items-center justify-end flex-1 min-w-0 gap-1"
    >
      <input
        v-model="editedValue"
        :type="inputType"
        class="!h-8 !mb-0 !text-sm"
        autofocus
        @keyup.enter="save"
        @keyup.esc="stopEditing"
      />
      <NextButton xs icon="i-lucide-check" @click="save" />
    </div>

    <button
      v-else
      type="button"
      class="flex items-center justify-end flex-1 min-w-0 gap-1.5 text-sm font-normal text-n-slate-11 hover:text-n-slate-12"
      :class="{ 'cursor-default hover:text-n-slate-11': !editable }"
      @click="startEditing"
    >
      <a
        v-if="href && value"
        :href="href"
        class="min-w-0 truncate text-n-brand hover:no-underline"
        @click.stop
      >
        {{ value }}
      </a>
      <span v-else class="min-w-0 truncate">
        {{ value || t('CONTACT_PANEL.NOT_AVAILABLE') }}
      </span>
      <NextButton
        v-if="showCopy && value"
        v-tooltip="t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
        xs
        slate
        ghost
        icon="i-lucide-clipboard"
        class="flex-shrink-0 hidden group-hover:flex"
        @click="onCopy"
      />
      <span
        v-if="editable"
        class="flex-shrink-0 i-lucide-chevron-right size-3.5 text-n-slate-9"
      />
    </button>
  </div>
</template>
